<template>
  <div>
    <MobileNavBar class="block lg:hidden"></MobileNavBar>
    <NavBar
      class="hidden lg:block transition-[opacity,transform] duration-[500ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
      :class="viewerPhase === 'opening' || viewerPhase === 'open' ? 'opacity-0 pointer-events-none -translate-y-1' : 'opacity-100 pointer-events-auto translate-y-0'"
    ></NavBar>
    <FullScreenImage></FullScreenImage>
    <div class="main-area text-center w-full h-full min-h-screen items-center justify-center flex flex-col overflow-auto p-4 pt-12" id="main-area">
      <div class="flex flex-1 items-center justify-center">
        <Transition name="fade" mode="out-in">
          <router-view class="z-10 w-full" />
        </Transition>
      </div>
      <footer class="w-full justify-center mt-auto text-xs lg:text-sm text-muted-green z-10">
        <span>© {{ currentYear }} Clair Green. All rights reserved.</span>
      </footer>
      <div class="shapes">
        <div class="circles">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
        <div class="triangles">
          <div></div>
          <div></div>
        </div>
        <div class="pentagons">
          <div></div>
          <div></div>
        </div>
        <div class="hexagons">
          <div></div>
          <div></div>
        </div>
        <div class="flowers">
          <div></div>
          <div></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useImageViewer } from './features/composables/useImageViewer';
import NavBar from './features/components/NavBar.vue';
import MobileNavBar from './features/components/MobileNavBar.vue';
import FullScreenImage from './features/global/FullScreenImage.vue';

const currentYear = new Date().getFullYear();
const { viewerPhase } = useImageViewer();
</script>

<style>
#app {
  color: #4a6350;
}

html,
body {
  scrollbar-gutter: stable;
}

.main-area {
  background: linear-gradient(to bottom, #edf7ed, #dfeee0, #edf7ed);
}

h2 {
  font-size: 24px;
  color: #6a8073;
  text-shadow: 0px 0px 3px #edf7ed; 
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  margin-top: 8px;
  margin-bottom: 8px;
 
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-thumb {
  background: #4a6350;
  border-radius: 2px;
}

.shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100vh;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
}

.shapes > * {
  pointer-events: none;
}

.circles div {
  position: absolute;
  display: block;
  width: 20px;
  height: 20px;
  background: rgba(144, 183, 167, 0.4); 
  bottom: -150px;
  animation: animateCircle 25s linear infinite;
}

.triangles div {
  position: absolute;
  display: block;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 20px solid rgba(144, 183, 167, 0.4); 
  bottom: -150px;
  animation: animateShape 25s linear infinite;
}

.pentagons div {
  position: absolute;
  display: block;
  width: 40px;
  height: 40px;
  background: rgba(144, 183, 167, 0.4); 
  clip-path: polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%);
  bottom: -150px;
  animation: animateShape 25s linear infinite;
}

.hexagons div {
  position: absolute;
  display: block;
  width: 50px;
  height: 50px;
  background: rgba(144, 183, 167, 0.4); 
  clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  bottom: -150px;
  animation: animateShape 25s linear infinite;
}

@keyframes animateCircle {
  0% {
    opacity: 0.75;
    border-radius: 0;
    transform: translateY(0) rotate(0deg);
  }
  100% {
    opacity: 0.25;
    border-radius: 50%;
    transform: translateY(-200vh) rotate(270deg);
  }
}

@keyframes animateShape {
  0% {
    opacity: 0.75;
    transform: translateY(0) rotate(0deg);
  }
  100% {
    opacity: 0.25;
    transform: translateY(-200vh) rotate(360deg);
  }
}

.circles div:nth-child(1) {
  left: 25%;
  width: 80px;
  height: 80px;
  animation-delay: 0s;
}

.circles div:nth-child(2) {
  left: 70%;
  width: 20px;
  height: 20px;
  animation-delay: 4s;
}

.triangles div:nth-child(1) {
  left: 65%;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 20px solid rgba(144, 183, 167, 0.3);
  animation-delay: 0s;
}

.triangles div:nth-child(2) {
  left: 10%;
  border-left: 20px solid transparent; 
  border-right: 20px solid transparent;
  border-bottom: 40px solid rgba(144, 183, 167, 0.3);
  animation-delay: 2s;
  animation-duration: 18s;
}

.pentagons div:nth-child(1) {
  left: 40%;
  width: 60px;
  height: 60px;
  animation-delay: 4s;
  animation-duration: 22s;
}

.pentagons div:nth-child(2) {
  left: 85%;
  width: 80px;
  height: 80px;
  animation-delay: 0s;
  animation-duration: 15s;
} 

.hexagons div:nth-child(1) {
  left: 35%;
  width: 40px;
  height: 40px;
  animation-delay: 7s;
}

.hexagons div:nth-child(2) {
  left: 60%;
  width: 15px;
  height: 15px;
  animation-delay: 15s;
  animation-duration: 35s;
}

.circles div:nth-child(4) {
  left: 50%;
  width: 25px;
  height: 25px;
  animation-delay: 15s;
  animation-duration: 45s;
}

.flowers div {
  position: absolute;
  display: block;
  width: 70px;
  height: 70px;
  bottom: -150px;
  animation: animateShape 25s linear infinite;
  
  background:
    /* top */
    radial-gradient(circle at 50% 100%,
      rgba(144, 183, 167, 0.55) 0 27%,
      transparent 27%),
    /* bottom */
    radial-gradient(circle at 50% 0%,
      rgba(144, 183, 167, 0.55) 0 27%,
      transparent 27%),
    /* left */
    radial-gradient(circle at 100% 50%,
      rgba(144, 183, 167, 0.55) 0 27%,
      transparent 27%),
    /* right */
    radial-gradient(circle at 0% 50%,
      rgba(144, 183, 167, 0.55) 0 27%,
      transparent 27%);

  background-repeat: no-repeat;
  background-size: 100% 100%;
  border-radius: 50%;
}


.flowers div:nth-child(1) {
  left: 30%;
  width: 80px;
  height: 80px;
  animation-delay: 2s;
  animation-duration: 26s;
}

.flowers div:nth-child(2) {
  left: 75%;
  width: 50px;
  height: 50px;
  animation-delay: 8s;
  animation-duration: 20s;
}


nav {
  padding: 30px;
}

nav a {
  font-weight: bold;
  color: #4a6350; 
}

nav a.router-link-exact-active {
  color: #90b7a7; 
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 1s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.link-in-text {
  color:#4a6350;
  text-decoration: underline;
  font-weight: 500;
}

.link-in-text:hover {
  color: #6a8073;
  text-decoration: none;
}
</style>
